
import { Globe, Code, PenTool, Server, ShoppingCart, Search, Database, Monitor, Plane, Box } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

const services = [
  {
    icon: <Globe className="h-10 w-10 text-tech-blue" />,
    title: "Web Design",
    description: "Custom-crafted, responsive websites that engage users and reflect your brand identity perfectly."
  },
  {
    icon: <Search className="h-10 w-10 text-tech-purple" />,
    title: "SEO Optimization",
    description: "Strategic optimization to increase visibility, drive organic traffic, and improve search rankings."
  },
  {
    icon: <PenTool className="h-10 w-10 text-tech-pink" />,
    title: "Graphic Design",
    description: "Eye-catching visuals and brand assets that communicate your message with clarity and impact."
  },
  {
    icon: <Code className="h-10 w-10 text-tech-cyan" />,
    title: "Software Creation",
    description: "Custom software solutions tailored to your business needs, from web apps to desktop applications."
  },
  {
    icon: <Box className="h-10 w-10 text-tech-blue" />,
    title: "CAD Modeling",
    description: "Precision 3D modeling and technical design services for product development and manufacturing."
  },
  {
    icon: <Plane className="h-10 w-10 text-tech-purple" />,
    title: "Aerial Footage",
    description: "High-quality drone photography and videography for stunning aerial perspectives of your projects."
  },
  {
    icon: <Server className="h-10 w-10 text-tech-blue" />,
    title: "Server Maintenance",
    description: "Proactive server monitoring and maintenance to ensure optimal performance and security."
  },
  {
    icon: <ShoppingCart className="h-10 w-10 text-tech-purple" />,
    title: "E-commerce Solutions",
    description: "Complete online store setups with secure payment gateways, product management, and sales optimization."
  },
  {
    icon: <Database className="h-10 w-10 text-tech-pink" />,
    title: "Database Management",
    description: "Efficient database architecture, optimization, and maintenance for reliable data management."
  },
  {
    icon: <Monitor className="h-10 w-10 text-tech-cyan" />,
    title: "IT Consultation",
    description: "Expert guidance on technology strategy, infrastructure, and digital transformation."
  }
];

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <section id="services" ref={sectionRef} className="py-20 px-6 bg-gradient-to-b from-background to-muted relative overflow-hidden">
      {/* Background elements for depth */}
      <div className="absolute inset-0 -z-10 opacity-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-tech-purple/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-tech-blue/20 rounded-full filter blur-3xl"></div>
        
        {/* Animated grid lines */}
        <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 gap-4 opacity-5">
          {Array.from({ length: 36 }).map((_, i) => (
            <div 
              key={i} 
              className="border border-foreground/50" 
              style={{ 
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${Math.random() * 4 + 2}s`
              }}
            />
          ))}
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-block rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary animate-pulse-slow">
            Our Expertise
          </div>
          <h2 className="text-3xl md:text-4xl font-bold">
            Comprehensive <span className="tech-gradient-text">Tech Solutions</span>
          </h2>
          <p className="text-foreground/70 max-w-2xl mx-auto">
            From design to development, BakerTech provides end-to-end technology services to help your business thrive in the digital landscape.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {services.map((service, index) => (
            <div 
              key={index}
              className={`service-card shadow-lg hover:shadow-xl transform transition-all ${isVisible ? 'animate-fade-in' : 'opacity-0'} ${hoveredIndex === index ? 'animated-border' : ''}`}
              style={{ 
                animationDelay: `${index * 0.1}s`,
                transform: `translateZ(${index % 3 * 5}px)`,
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className={`mb-4 p-3 rounded-full bg-background/50 w-16 h-16 flex items-center justify-center shadow-inner ${hoveredIndex === index ? 'animate-bounce-slow' : ''}`}>
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-2 relative">
                {service.title}
                {hoveredIndex === index && (
                  <span className="absolute -right-1 -top-1 w-2 h-2 bg-primary rounded-full animate-ping"></span>
                )}
              </h3>
              <p className="text-foreground/70">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
